import { Box, Button, Group, Stack, Text, TextInput, Paper, ActionIcon, FileButton } from '@mantine/core';
import { IconPlus, IconX, IconFile } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useRef } from 'react';
import { FileUploadItem } from './types/ObjectFormTypes';
import { useTranslation } from "../../hooks/useTranslation";
import useTransientStore from "../../data/transcientStore";

interface FileUploadListProps {
    files: FileUploadItem[];
    onChange: (files: FileUploadItem[]) => void;
    label?: string;
    description?: string;
    maxFiles?: number;
    maxFileSize?: number; // in bytes
    accept?: string;
    disabled?: boolean;
}

export default function FileUploadList({
    files, 
    onChange, 
    label, 
    description,
    maxFiles = Infinity,
    maxFileSize = 30 * 1024 * 1024, // 30MB default
    accept,
    disabled = false
}: FileUploadListProps) {
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;

    const { getTranslation } = useTranslation({ pageId: 348, addressSystemId: addrIdFromStore || 0, autoLoad: true });

    const labelText = label ?? getTranslation("LABEL_FILES") ?? "Files";

    const resetRef = useRef<() => void>(null);

    const handleFileSelect = (file: File | null) => {
        if (!file) return;

        // Check if file is empty (0 bytes)
        if (file.size === 0) {
            notifications.show({
                title: getTranslation("TITLE_INVALID_FILE") || 'Invalid file',
                message:
                    getTranslation("MESSAGE_EMPTY_FILE_NOT_ALLOWED") ||
                    'Cannot upload an empty file (0 bytes).',
                color: 'red',
                position: 'bottom-left',
                autoClose: 5000,
                classNames: {
                    root: 'notification-error',
                },
            });
            // Reset the file input to allow selecting the same file again
            resetRef.current?.();
            return;
        }

        // Check file size
        if (file.size > maxFileSize) {
            const maxSizeMB = maxFileSize / (1024 * 1024);
            notifications.show({
                title: getTranslation("TITLE_FILE_TOO_LARGE") || 'File too large',
                message:
                    getTranslation("MESSAGE_FILE_TOO_LARGE") ||
                    `File size exceeds the maximum limit of ${maxSizeMB}MB. Selected file is ${formatFileSize(file.size)}.`,
                color: 'red',
                position: 'bottom-left',
                autoClose: 5000,
                classNames: {
                    root: 'notification-error',
                },
            });
            // Reset the file input to allow selecting the same file again
            resetRef.current?.();
            return;
        }

        // Check max files limit
        if (files.length < maxFiles) {
            const newFileItem: FileUploadItem = {
                id: `${Date.now()}-${Math.random()}`, // Generate unique ID
                file: file,
                // fileHeader: file.name.replace(/\.[^/.]+$/, '') // Remove extension for default header
                fileHeader: ''
            };
            onChange([...files, newFileItem]);
            
            // Reset the file input to allow selecting the same file again
            resetRef.current?.();
        }
    };

    const handleHeaderChange = (id: string, newHeader: string) => {
        const updatedFiles = files.map(item => 
            item.id === id ? { ...item, fileHeader: newHeader } : item
        );
        onChange(updatedFiles);
    };

    const handleRemoveFile = (id: string) => {
        const updatedFiles = files.filter(item => item.id !== id);
        onChange(updatedFiles);
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return getTranslation("LABEL_ZERO_BYTES") || '0 Bytes';
        const k = 1024;
        const sizes = [
            getTranslation("LABEL_BYTES") || 'Bytes',
            getTranslation("LABEL_KB") || 'KB',
            getTranslation("LABEL_MB") || 'MB',
            getTranslation("LABEL_GB") || 'GB'
        ];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    return (
        <Stack gap="md">
            <Box>
                <Group justify="space-between" align="flex-end" mb={4}>
                    <Group gap="xs">
                        <Text size="sm" fw={500}>{labelText}</Text>
                        {files.length > 0 && (
                            <Text size="xs" c="dimmed" fw={500}>
                                ({files.length}{maxFiles !== Infinity ? `/${maxFiles}` : ''})
                            </Text>
                        )}
                    </Group>
                    <Text size="xs" c="dimmed">
                        {getTranslation("LABEL_MAX_PER_FILE") || "Max per file:"}{" "}
                        {formatFileSize(maxFileSize)}
                    </Text>
                </Group>
                {description && (
                    <Text size="xs" c="dimmed">{description}</Text>
                )}
            </Box>

            {files.length > 0 && (
                <Box 
                    style={{ 
                        maxHeight: files.length > 3 ? '400px' : 'none',
                        overflowY: files.length > 3 ? 'auto' : 'visible',
                        paddingRight: files.length > 3 ? '4px' : '0'
                    }}
                >
                    <Stack gap="xs">
                        {files.map((fileItem) => (
                            <Paper key={fileItem.id} p="md" withBorder radius="md">
                                <Group gap="sm" mb="sm" wrap="nowrap">
                                    <IconFile size={18} style={{ flexShrink: 0 }} />
                                    <Box style={{ flex: 1, minWidth: 0 }}>
                                        <Text size="sm" fw={500} truncate>
                                            {fileItem.file.name}
                                        </Text>
                                        <Text size="xs" c="dimmed">
                                            {formatFileSize(fileItem.file.size)}
                                        </Text>
                                    </Box>
                                    <ActionIcon 
                                        color="red" 
                                        variant="subtle"
                                        onClick={() => handleRemoveFile(fileItem.id)}
                                        size="sm"
                                        disabled={disabled}
                                    >
                                        <IconX size={16} />
                                    </ActionIcon>
                                </Group>
                                <TextInput
                                    placeholder={getTranslation("PLACEHOLDER_ADD_FILE_HEADER") || "Add file header"}
                                    value={fileItem.fileHeader}
                                    onChange={(e) => handleHeaderChange(fileItem.id, e.currentTarget.value)}
                                    size="sm"
                                    disabled={disabled}
                                />
                            </Paper>
                        ))}
                    </Stack>
                </Box>
            )}

            {files.length < maxFiles ? (
                <FileButton onChange={handleFileSelect} accept={accept} resetRef={resetRef} disabled={disabled}>
                    {(props) => (
                        <Button
                            {...props}
                            variant="light"
                            leftSection={<IconPlus size={18} />}
                            fullWidth
                            size="sm"
                            disabled={disabled}
                        >
                            {files.length === 0
                                ? (getTranslation("LABEL_ADD_FILE") || 'Add File')
                                : (getTranslation("LABEL_ADD_ANOTHER_FILE") || 'Add Another File')}
                            {maxFiles !== Infinity && ` (${files.length}/${maxFiles})`}
                        </Button>
                    )}
                </FileButton>
            ) : (
                <Text size="xs" c="dimmed" ta="center" py="xs">
                    {getTranslation("LABEL_MAX_FILES_REACHED_PREFIX") || "Maximum of"}{" "}
                    {maxFiles}{" "}
                    {maxFiles === 1
                        ? (getTranslation("LABEL_FILE_SINGULAR") || 'file')
                        : (getTranslation("LABEL_FILE_PLURAL") || 'files')}{" "}
                    {getTranslation("LABEL_MAX_FILES_REACHED_SUFFIX") || "reached"}
                </Text>
            )}
        </Stack>
    );
}
